import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatZAR } from "../lib/api";
import { vehiclePhoto } from "../lib/photos";

const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const priceIcon = (price, active) =>
  L.divIcon({
    className: "",
    html: `<div class="tadada-pin ${active ? "tadada-pin-active" : ""}">${formatZAR(price)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
  }, [map, points]);
  return null;
}

export default function VehicleMap({ vehicles, activeId, onHover, className = "" }) {
  const placed = useMemo(() => vehicles.filter((v) => v.lat != null && v.lng != null), [vehicles]);
  const points = useMemo(() => placed.map((v) => [v.lat, v.lng]), [placed]);

  return (
    <div className={`relative rounded-3xl overflow-hidden border border-[#E2E8F0] ${className}`} data-testid="vehicle-map">
      <MapContainer center={[-29.0, 24.0]} zoom={5} scrollWheelZoom className="w-full h-full" style={{ minHeight: 420 }}>
        <TileLayer url={TILES} attribution={ATTR} />
        <FitBounds points={points} />
        {placed.map((v) => (
          <Marker
            key={v.id}
            position={[v.lat, v.lng]}
            icon={priceIcon(v.price_per_day, v.id === activeId)}
            eventHandlers={{ mouseover: () => onHover?.(v.id), mouseout: () => onHover?.(null) }}
          >
            <Popup closeButton={false} className="tadada-popup">
              <Link to={`/vehicles/${v.id}`} className="block w-52" data-testid={`map-popup-${v.id}`}>
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F2F5]">
                  <img src={vehiclePhoto(v)} alt={v.title} className="w-full h-full object-cover" />
                </div>
                <p className="font-semibold text-[#0a1f4d] text-sm mt-2 leading-tight">{v.brand} {v.model}</p>
                <p className="text-xs text-[#64748B]">{v.location_text}, {v.city}{v.approx_location ? " · approx." : ""}</p>
                <p className="text-sm font-bold text-[#0a1f4d] mt-1">{formatZAR(v.price_per_day)} <span className="text-xs font-normal text-[#64748B]">/day</span></p>
              </Link>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="absolute bottom-3 left-3 z-[400] bg-white/90 backdrop-blur text-[11px] text-[#64748B] px-2.5 py-1 rounded-full border border-[#E2E8F0]">
        {placed.length} car{placed.length === 1 ? "" : "s"} on map
      </div>
    </div>
  );
}
